import LogRocket from 'logrocket';
import template from './eventDetailsData.html';
import _ from 'lodash';
import { dateTimeIfDate } from '../../../core/utils/filters';
import { differenceInMinutes } from 'date-fns';

const TRIPWIRES_TRIPPED_EVENT = 'tripwires/tripped';

/*
  Directive created to display the event details pulled from the eventDetailsController
*/

export default {
  name: 'eventDetails',
  template,
  controller: EventDetailsController,
  controllerAs: 'eventDetailsCtrl',
  bindings: {
    theEvent: '<',
    panel: '<?',
    activeItemChange: '<?'
  }
};

/* @ngInject */
function EventDetailsController(
  $element,
  $scope,
  EventInfoService,
  growl,
  EventsPanelService,
  $state,
  appConfig,
  UserInfoService,
  EquipmentInfoService,
  VoiceDownPanelService,
  ModalService,
  SiteService
) {
  const vm = this;

  const SPECIAL_EVENT_DATA_KEYS = new Set(['media', 'summary']);

  vm.$onChanges = $onChanges;
  vm.flagThisEvent = flagThisEvent;
  vm.dismissEvent = dismissEvent;
  vm.canDelete = canDelete;
  vm.deleteThisEvent = deleteThisEvent;
  vm.saveEventNotes = saveEventNotes;
  vm.isArray = _.isArray;
  vm.startCase = _.startCase;
  vm.acknowledgeThisEvent = acknowledgeThisEvent;
  vm.baseUrl = appConfig.appBaseUrl;
  vm.getImportantDataToDisplay = getImportantDataToDisplay;
  vm.importantAttributes = EventInfoService.eventsConfig.importantAttributes;
  vm.eventId = null;
  vm.media = [];
  vm.onShowCameraChange = onShowCameraChange;
  vm.showCamera = false;
  vm.$onInit = $onInit;
  vm.notSpecialOrImportantKey = notSpecialOrImportantKey;

  vm.speakers = null;
  vm.selectedSpeaker = null;
  vm.shouldShowVoicedowns = false;
  vm.trespassingClipId = null;
  vm.handlePlayClick = handlePlayClick;
  vm.playClip = playClip;

  function onShowCameraChange($event) {
    $scope.$apply(() => {
      vm.showCamera = $event;
    });
  }

  function $onInit() {
    $scope.$on('event:updated', (__, event) => {
      if (vm.theEvent.id !== event.id) {
        return;
      }
      // event was updated by the API, check for new important data
      vm.importantData = getImportantDataToDisplay(vm.theEvent.eventName);
      // event was updated by the API, check for new media
      prepareMedia();
    });
  }

  async function $onChanges({ theEvent }) {
    // onChanges is triggered when the reference of `theEvent` changes
    // since we update the existing reference of an event with API media updates,
    // it is safe to assume this reference change is when a new/different event is selected
    if (!theEvent || !theEvent?.currentValue) { return }
    $element[0].children[0].scroll(0, 0);

    const currentEventId = theEvent.currentValue.id;
    const eq = theEvent.currentValue.equipment;

    vm.selectedSpeaker = null;
    vm.speakers = [];
    vm.shouldShowVoicedowns = false;
    vm.shouldShowSiteInfo = false;
    vm.site = null;
    vm.trespassingClipId = null;

    let speakers = [];
    let site = null;
    let trespassingClipId = null;
    try {
      const rawSpeakers = eq?.settings?.speakers ?
        await EquipmentInfoService.getEquipmentById(eq.settings.speakers) :
        EquipmentInfoService.getAssociatedEquipment(eq?.id, 'speakers');
      speakers = [rawSpeakers].flat().filter(Boolean);
      site = eq?.site && await SiteService.get(eq.site.id || eq.site);
      if (speakers.length > 0) {
        trespassingClipId = await VoiceDownPanelService.getClipKeyForName(speakers[0].id, 'trespassing');
      }
    } catch (err) {
      LogRocket.track(err?.error || err?.message || err);
    }

    if (vm.theEvent?.id !== currentEventId) { return }

    $scope.$evalAsync(() => {
      vm.selectedSpeaker = null;
      vm.speakers = speakers;
      if (vm.speakers?.length === 1) { vm.selectedSpeaker = vm.speakers[0] }
      vm.shouldShowVoicedowns = vm.speakers?.length && vm.theEvent?.eventName.includes('cameras/');
      vm.shouldShowSiteInfo = vm.shouldShowVoicedowns && !_.isEmpty(site?.info);
      vm.site = site;
      vm.trespassingClipId = trespassingClipId;
    });

    prepareMedia();

    if (vm.activeItemChange) {
      vm.activeItemChange.emit({
        direction: 'reset'
      });
    }
    vm.importantData = getImportantDataToDisplay(vm.theEvent.eventName);
  }

  function prepareMedia() {
    const videoMedia = vm.theEvent?.data?.media?.videos?.map((video) => {
      const { video: name, mp4, webm } = video; // video filename without extension
      // find the first thumbnail from the video to use
      const thumb = _.find(vm.theEvent.data.media.thumbs, ({ sslUrl }) => sslUrl.includes(`${name}_thumbs`));
      const formats = [];
      if (mp4) {
        formats.push({
          sslUrl: mp4.sslUrl,
          type: 'mp4'
        });
      }
      if (webm) {
        formats.push({
          sslUrl: webm.sslUrl,
          type: 'webm'
        });
      }
      return {
        sslUrl: thumb?.sslUrl,
        video: name,
        formats
      };
    }).sort((a, b) => {
      const [aFile, bFile] = [a.video, b.video].map(file => (file || '').toString().toLowerCase());
      return aFile.localeCompare(bFile);
    });

    let imageMedia = vm.theEvent?.data?.media?.images?.map(item => item);
    imageMedia = imageMedia?.sort((a, b) => {
      const [aFile, bFile] = [a.sslUrl, b.sslUrl].map(url => (new URL(url)?.pathname || '').split('/').pop());
      return aFile.localeCompare(bFile);
    });

    vm.media = [...videoMedia || [], ...imageMedia || []];
    // use and set this intermediate variable so that the galleria ngOnChanges fires at the same time as media
    vm.eventId = vm.theEvent.id;
  }

  function flagThisEvent(event) {
    event.flagged = !event.flagged;

    EventInfoService.updateEvent(event.id, { flagged: event.flagged })
      .catch(() => {
        event.flagged = !event.flagged;
      });
  }

  function dismissEvent(event) {
    event.dismissed = !event.dismissed;

    EventInfoService.updateEvent(event.id, { dismissed: event.dismissed })
      .then(() => {
        // Broadcast the event update to ensure all components are in sync
        // Add more detail to help event panel recognize this is a tripwire event
        const updatedEvent = {
          ...event,
          _source: 'tripwirePanel' // Add a source marker
        };
        $scope.$root.$broadcast('event:updated', updatedEvent);
      })
      .catch(() => {
        event.dismissed = !event.dismissed;
      });
  }

  // TODO RBAC
  function canDelete() {
    const hasPermissions = UserInfoService.isCurrentUserSuperAdmin() || UserInfoService.isCurrentUserPartner();
    return vm.theEvent?.eventName !== TRIPWIRES_TRIPPED_EVENT && hasPermissions;
  }

  async function deleteThisEvent(event = vm.theEvent) {
    let growlMessage = growl.info('Deleting Event...');
    try {
      const { EventDetailsModalCtrl: ctrl } = $scope.$parent;
      if (ctrl) {
        await ctrl.deleteCurrentEvent(event);
      } else {
        // If the modal is not the parent, we are on the event details page
        await EventInfoService.deleteEvent(event.id);
        $state.go('app');
      }
      growl.success('Event deleted.');
    } catch (err) {
      growlMessage = growl.error(err.error || err, { title: 'Error deleting event' });
      LogRocket.track(err.error || err);
    } finally {
      growlMessage.destroy();
    }
  }

  function saveEventNotes(event) {
    let growlMessage = growl.info('Saving Notes...', { disableCloseButton: true });

    EventInfoService.updateEvent(event.id, { notes: event.notes })
      .then(() => {
        growlMessage.destroy();
        growlMessage = growl.success('Event notes saved!');
      })
      .catch((res) => {
        growlMessage.destroy();
        const error = 'Error while saving notes';
        growlMessage = growl.error(res.error, { title: error });
      });
  }

  async function acknowledgeThisEvent(event) {
    const updatedEvent = await EventsPanelService.acknowledgeThisEvent(event);
    if (updatedEvent) {
      vm.theEvent = updatedEvent;
      growl.success('Event acknowledged.', { ttl: 4000 });
    }
  }

  function getImportantDataToDisplay(eventName) {
    return _.reduce(vm.importantAttributes[eventName], (acc, att) => {
      if (att) {
        const key = att.title || _.startCase(att);
        let val = _.get(vm.theEvent.data, att.path ? att.path : att);
        let preformatted = false;
        if (_.isObject(val)) {
          // Make it a string, better than [object Object]
          val = JSON.stringify(val, (__key, value) => dateTimeIfDate(value), 2);
          preformatted = true;
        } else {
          val = dateTimeIfDate(val);
        }
        acc[key] = {
          val,
          preformatted
        };
      }
      return acc;
    }, {});
  }

  function notSpecialOrImportantKey(key) {
    return !(SPECIAL_EVENT_DATA_KEYS.has(key) || _.has(vm.importantData, key));
  }

  function playClip(equipmentId, clipId) {
    let growlMessage;
    if (equipmentId && clipId) {
      // growl playing selected clip true
      growlMessage = growl.info('Connecting to speaker', {
        disableCloseButton: true
      });
      VoiceDownPanelService.playClip(equipmentId, clipId)
        .then(() => {
          growlMessage.destroy();
          growl.success('Clip played');
        })
        .catch((res) => {
          growlMessage.destroy();
          const error = 'Error Playing Clip';
          growl.error(res.error, { title: error });
          LogRocket.track(error);
        });
    }
  }

  function handlePlayClick(clipId = vm.trespassingClipId || 'trespassing') {
    if (!vm.selectedSpeaker) { return }
    const eventDate = new Date(vm.theEvent.eventDate);
    const showConfirmationModal = differenceInMinutes(new Date(), eventDate) >= 5;
    if (!showConfirmationModal) {
      return playClip(vm.selectedSpeaker.id, clipId);
    }

    return ModalService.confirmationModal({
      modalTitle: 'Confirm',
      selectionCriteria: 'This event occurred more than 5 minutes ago, are you sure you want to play the clip?',
      onConfirm: () => playClip(vm.selectedSpeaker.id, clipId)
    });
  }
}

